// src/MainApp.js
import React, { useContext, useEffect } from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet,
    useLocation,
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthContext } from './contexts/AuthContext';
import { UserContext } from './contexts/UserContext';
import Login from './components/login/Login';
import UserSelection from './components/user_selection/UserSelection';
import Dashboard from './components/dashboard/Dashboard';
import PlayersPage from './components/players/PlayersPage';
import GroupsPage from './components/groups/GroupsPage';
import GamesPage from './components/games/GamesPage';
import ContentsPage from './components/contents/ContentsPage';
import ChallangerPage from './components/challanger/ChallangerPage';
import QuizPage from './components/quiz/QuizPage';
import StorePage from './components/store/StorePage';
import UserContentPage from './components/userContents/UserContentPage';
import PurchasePage from './components/purchases/PurchasePage';
import ForgotPasswordPage from './components/password/ForgotPasswordPage';
import ResetPasswordPage from './components/password/ResetPasswordPage';
import DashboardHome from './components/dashboard/DashboardHome';
import SettingsPage from './components/settings/SettingsPage';
import CardsPage from './components/cards/CardsPage';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminHome from './components/admin/AdminHome';
import AdminContentPage from './components/admin/AdminContentPage';
import AdminGroupPage from './components/admin/AdminGroupPage';
import AdminGamesPage from './components/admin/AdminGamesPage';
import AdminProductPage from './components/admin/AdminProductPage';
import ContentsHistoryPage from './components/contents/ContentsHistory';
import PurchaseHistoryPage from './components/purchases/PurchaseHistory';
import QuizHistoryPage from './components/quiz/QuizHistory';
import AdminPlansPage from './components/admin/AdminPlansPage';

const ProtectedRoute = ({ condition, redirectTo }) => {
    return condition ? <Outlet /> : <Navigate to={redirectTo} />;
};

const TitleUpdater = () => {
    const location = useLocation();

    useEffect(() => {
        const pageTitles = {
            '/login': 'Login - GameUP',
            '/select-user': 'Selecionar usuário - GameUP',
            '/reset-password': 'Recuperar Senha - GameUP',
            '/dashboard': 'Dashboard - GameUP',
            '/dashboard/players/list': 'Gerenciar Jogadores - GameUP',
            '/dashboard/players/groups': 'Gerenciar Grupos - GameUP',
            '/dashboard/players/games': 'Gerenciar Jogos - GameUP',
            '/dashboard/contents/manage': 'Gerenciar Missões - GameUP',
            '/dashboard/contents/requests': 'Gerenciar Requisições - GameUP',
            '/dashboard/challange/manage': 'Gerenciar Desafios - GameUP',
            '/dashboard/store/products': 'Gerenciar Produtos - GameUP',
            '/dashboard/store/purchases': 'Gerenciar Compras - GameUP',
        };

        document.title = pageTitles[location.pathname] || 'GameUP';
    }, [location]);

    return null;
};

const MainApp = () => {
    const { token, loading, role } = useContext(AuthContext);
    const { user } = useContext(UserContext);

    if (loading) {
        return <div>Carregando...</div>;
    }

    const isSuperAdmin = role === 'SUPERADMIN';

    return (
        <>
            <Router>
                <TitleUpdater />
                <Routes>
                    <Route
                        path="/login"
                        element={
                            isSuperAdmin ? (
                                <Navigate to="/admin" />
                            ) : !token ? (
                                <Login />
                            ) : (
                                <Navigate to="/select-user" />
                            )
                        }
                    />

                    <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                    />
                    <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                    />

                    {/* SUPERADMIN */}
                    <Route
                        element={
                            <ProtectedRoute
                                condition={token && role === 'SUPERADMIN'}
                                redirectTo="/login"
                            />
                        }
                    >
                        <Route path="/admin" element={<AdminDashboard />}>
                            <Route index element={<AdminHome />} />
                            <Route path="plans" element={<AdminPlansPage />} />
                            <Route
                                path="contents"
                                element={<AdminContentPage />}
                            />
                            <Route path="groups" element={<AdminGroupPage />} />
                            <Route path="games" element={<AdminGamesPage />} />
                            <Route
                                path="products"
                                element={<AdminProductPage />}
                            />
                        </Route>
                    </Route>

                    {/* ADMINS/MODERATORS */}
                    {!isSuperAdmin && (
                        <>
                            <Route
                                path="/select-user"
                                element={
                                    token ? (
                                        !user ? (
                                            <UserSelection />
                                        ) : (
                                            <Navigate to="/dashboard" />
                                        )
                                    ) : (
                                        <Navigate to="/login" />
                                    )
                                }
                            />
                            <Route
                                element={
                                    <ProtectedRoute
                                        condition={token && user}
                                        redirectTo="/login"
                                    />
                                }
                            >
                                <Route
                                    path="/dashboard"
                                    element={<Dashboard />}
                                >
                                    <Route index element={<DashboardHome />} />
                                    <Route
                                        path="players/list"
                                        element={<PlayersPage />}
                                    />
                                    <Route
                                        path="players/cards"
                                        element={<CardsPage />}
                                    />
                                    <Route
                                        path="players/groups"
                                        element={<GroupsPage />}
                                    />
                                    <Route
                                        path="players/games"
                                        element={<GamesPage />}
                                    />
                                    <Route
                                        path="contents/manage"
                                        element={<ContentsPage />}
                                    />
                                    <Route
                                        path="contents/requests"
                                        element={<UserContentPage />}
                                    />
                                    <Route
                                        path="contents/history"
                                        element={<ContentsHistoryPage />}
                                    />
                                    {/* <Route
                                        path="challange/manage"
                                        element={<ChallangerPage />}
                                    /> */}
                                    <Route
                                        path="quiz/manage"
                                        element={<QuizPage />}
                                    />
                                    <Route
                                        path="quiz/history"
                                        element={<QuizHistoryPage />}
                                    />
                                    <Route
                                        path="store/products"
                                        element={<StorePage />}
                                    />
                                    <Route
                                        path="store/purchases"
                                        element={<PurchasePage />}
                                    />
                                    <Route
                                        path="store/history"
                                        element={<PurchaseHistoryPage />}
                                    />
                                    <Route
                                        path="settings"
                                        element={<SettingsPage />}
                                    />
                                </Route>
                            </Route>
                        </>
                    )}

                    {/* Default fallback */}
                    <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
            </Router>

            {/* <Router>
                <TitleUpdater />
                <Routes>
                    <Route
                        path="/login"
                        element={
                            !token ? <Login /> : <Navigate to="/select-user" />
                        }
                    />

                    <Route
                        path="/select-user"
                        element={
                            token ? (
                                !user ? (
                                    <UserSelection />
                                ) : (
                                    <Navigate to="/dashboard" />
                                )
                            ) : (
                                <Navigate to="/login" />
                            )
                        }
                    />

                    <Route
                        element={
                            <ProtectedRoute
                                condition={token && user}
                                redirectTo="/login"
                            />
                        }
                    >
                        <Route path="/dashboard" element={<Dashboard />}>
                            <Route index element={<DashboardHome />} />
                            <Route
                                path="players/list"
                                element={<PlayersPage />}
                            />
                            <Route
                                path="players/cards"
                                element={<CardsPage />}
                            />
                            <Route
                                path="players/groups"
                                element={<GroupsPage />}
                            />
                            <Route
                                path="players/games"
                                element={<GamesPage />}
                            />
                            <Route
                                path="contents/manage"
                                element={<ContentsPage />}
                            />
                            <Route
                                path="contents/requests"
                                element={<UserContentPage />}
                            />
                            <Route
                                path="store/products"
                                element={<StorePage />}
                            />
                            <Route
                                path="store/purchases"
                                element={<PurchasePage />}
                            />
                            <Route path="settings" element={<SettingsPage />} />
                        </Route>
                    </Route>

                    <Route path="*" element={<Navigate to="/login" />} />
                    <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                    />
                    <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                    />
                </Routes>
            </Router> */}
        </>
    );
};

export default MainApp;
